/**
 * Talking to the API.
 *
 * Note what is missing: there is no `addItem`, no `patchItem`, no `purchase`. Every mutation
 * to the *inventory* goes through `push`, the batched sync route, because every one of them is
 * queued locally first. One write path means the app is offline-correct by construction rather
 * than by remembering to catch a failure at each call site.
 *
 * Reminders and household settings are the exception, and they are direct calls for the same
 * reason the server keeps them out of its event log: nothing converges on them and no other
 * device replays them. They are also the two things a user changes while sitting in a settings
 * screen, where being told "not while you are offline" is a fair answer.
 */

import type {
  Food,
  FoodLookup,
  Grocery,
  InventoryItem,
  Location,
  PreferenceChange,
  Preferences,
  QueuedMutation,
  ShoppingEntry,
  SyncDelta,
  SyncReceipt,
} from './types';
import type { Credentials } from './session';

/** The request could not be sent, or came back as something other than an answer. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Whether waiting and trying the same request again could plausibly work. */
  get isTransient(): boolean {
    return this.status === null || this.status >= 500 || this.status === 429;
  }

  /** Whether the household needs to sign in again. */
  get needsSignIn(): boolean {
    return this.status === 401;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * How long the food lookup may take before the app stops waiting for it.
 *
 * Sized against the Lambda behind it rather than against anybody's patience: that function
 * gives up on a wedged model call at twenty seconds, so a deadline shorter than its own would
 * throw away answers that were on their way — and a discarded answer is not merely a slow one,
 * because the entry it would have filled in is written as `unknown` and never asked about
 * again. This is the backstop for when even the server's own surrender does not arrive.
 */
export const LOOKUP_DEADLINE_MS = 25_000;

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly credentials: Credentials,
  ) {}

  inventory(): Promise<InventoryItem[]> {
    return this.get('/v1/inventory');
  }

  locations(): Promise<Location[]> {
    return this.get('/v1/locations');
  }

  groceries(): Promise<Grocery[]> {
    return this.get('/v1/groceries');
  }

  shopping(): Promise<ShoppingEntry[]> {
    return this.get('/v1/shopping');
  }

  foods(): Promise<Food[]> {
    return this.get('/v1/foods');
  }

  /**
   * Asks where an unfamiliar food goes and how long it keeps.
   *
   * A read, and the one call in this client that costs money — it reaches a model only for a
   * name the seeded catalog has never heard of. The answer is written straight into the
   * household's own list, so a food is looked up once and never again.
   *
   * Failing is survivable and the caller is expected to handle it: the food is recorded as
   * needing a look and the item still lands, which is the whole point of a local-first client.
   */
  lookUpFood(name: string): Promise<FoodLookup> {
    return this.send('POST', '/v1/foods/lookup', { name }, LOOKUP_DEADLINE_MS);
  }

  preferences(): Promise<Preferences> {
    return this.get('/v1/household');
  }

  /** Changes some of them, and answers with the settings as they now stand. */
  changePreferences(change: PreferenceChange): Promise<Preferences> {
    return this.send('PATCH', '/v1/household', change);
  }

  delta(since: number): Promise<SyncDelta> {
    return this.get(`/v1/sync?since=${since}`);
  }

  /**
   * Drains a batch of queued mutations.
   *
   * Each carries its own idempotency key, so resending a batch that partly landed redoes only
   * the part that did not.
   */
  push(mutations: QueuedMutation[]): Promise<SyncReceipt> {
    return this.send('POST', '/v1/sync', { mutations });
  }

  /**
   * Registers this device for reminders.
   *
   * No idempotency key: the endpoint is the identity, so sending it twice writes the same
   * record. The server documents this as the one exception to the rule.
   */
  async subscribeToReminders(subscription: unknown): Promise<void> {
    await this.send('PUT', '/v1/push/subscription', subscription);
  }

  async stopReminders(endpoint: string): Promise<void> {
    await this.send('DELETE', '/v1/push/subscription', { endpoint });
  }

  private get<T>(path: string): Promise<T> {
    return this.send<T>('GET', path);
  }

  /**
   * The one request shape, with an optional deadline in milliseconds.
   *
   * Only the food lookup sets one. Every other route here answers a tap out of DynamoDB and is
   * expected back in milliseconds, and a queued mutation that hangs costs nobody anything —
   * they are already in the outbox and the next attempt sends them. The lookup is the one call
   * somebody stands and waits for, so it is the one that needs a point at which to stop.
   */
  private async send<T>(
    method: string,
    path: string,
    body?: unknown,
    deadlineMs?: number,
  ): Promise<T> {
    const token = await this.credentials.bearer();
    if (token === null) {
      throw new ApiError('not signed in', 401, 'unauthorized');
    }

    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        // Aborted rather than merely stopped waiting for, so a request nobody will read is
        // off the wire instead of holding a connection open behind whatever comes next.
        ...(deadlineMs === undefined ? {} : { signal: AbortSignal.timeout(deadlineMs) }),
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      // A pantry in a basement is the normal case, not the exceptional one. Offline and a
      // request that ran out of time are both reported as transient, so the queue waits
      // rather than discarding work.
      throw new ApiError(describeUnreachable(cause, path), null, null);
    }

    if (!response.ok) {
      throw await describeFailure(response);
    }

    // A 204 carries no body, and JSON.parse of nothing is an error rather than an empty answer.
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

/** Why a request never came back, said in terms of what the caller can do about it. */
function describeUnreachable(cause: unknown, path: string): string {
  return cause instanceof DOMException && cause.name === 'TimeoutError'
    ? `${path} did not answer in time`
    : `could not reach the API: ${String(cause)}`;
}

async function describeFailure(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => ({}))) as ErrorBody;

  return new ApiError(
    body.error?.message ?? `the API answered ${response.status}`,
    response.status,
    body.error?.code ?? null,
  );
}
